import { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Badge,
  Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { usePurchaseOrders, useSupplierSearch } from '@hooks/usePurchasing';
import type {
  PurchaseOrderListItem,
  PurchaseOrderStatus,
  PurchaseOrderFilters,
} from '@/types/purchasing';

const { RangePicker } = DatePicker;

const MD_STATUS_OPTIONS: { value: PurchaseOrderStatus | ''; label: string }[] = [
  { value: '', label: 'Всі статуси' },
  { value: 'Draft', label: 'Чернетка' },
  { value: 'Submitted', label: 'Подано' },
  { value: 'Approved', label: 'Затверджено' },
  { value: 'PartiallyReceived', label: 'Частково отримано' },
  { value: 'Received', label: 'Отримано' },
  { value: 'Cancelled', label: 'Скасовано' },
];

const MD_STATUS_COLOR_MAP: Record<PurchaseOrderStatus, string> = {
  Draft: 'default',
  Submitted: 'processing',
  Approved: 'warning',
  PartiallyReceived: 'orange',
  Received: 'success',
  Cancelled: 'error',
};

const MD_STATUS_LABEL_MAP: Record<PurchaseOrderStatus, string> = {
  Draft: 'Чернетка',
  Submitted: 'Подано',
  Approved: 'Затверджено',
  PartiallyReceived: 'Частково отримано',
  Received: 'Отримано',
  Cancelled: 'Скасовано',
};

interface PurchaseOrdersPageProps {
  onCreateOrder: () => void;
  onViewOrder: (mdOrderId: string) => void;
  onEditOrder: (mdOrderId: string) => void;
}

export default function PurchaseOrdersPage({
  onCreateOrder,
  onViewOrder,
  onEditOrder,
}: PurchaseOrdersPageProps) {
  const [mdSearch, setMdSearch] = useState('');
  const [mdSupplierFilter, setMdSupplierFilter] = useState<string | undefined>();
  const [mdStatusFilter, setMdStatusFilter] = useState<PurchaseOrderStatus | ''>('');
  const [mdDateRange, setMdDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(
    null
  );
  const [mdSupplierSearchText, setMdSupplierSearchText] = useState('');
  const [mdPage, setMdPage] = useState(1);
  const [mdPageSize, setMdPageSize] = useState(20);

  const { data: mdSupplierOptions } = useSupplierSearch(mdSupplierSearchText);

  const mdFilters: PurchaseOrderFilters = useMemo(() => ({
    search: mdSearch || undefined,
    supplierId: mdSupplierFilter,
    status: mdStatusFilter || undefined,
    dateFrom: mdDateRange?.[0]?.format('YYYY-MM-DD'),
    dateTo: mdDateRange?.[1]?.format('YYYY-MM-DD'),
    page: mdPage,
    pageSize: mdPageSize,
  }), [mdSearch, mdSupplierFilter, mdStatusFilter, mdDateRange, mdPage, mdPageSize]);

  const { data: mdOrdersData, isLoading: mdIsLoading } = usePurchaseOrders(mdFilters);

  const mdColumns: ColumnsType<PurchaseOrderListItem> = [
    {
      title: 'Номер',
      dataIndex: 'number',
      key: 'number',
      width: 140,
      sorter: (mdA, mdB) => mdA.number.localeCompare(mdB.number),
    },
    {
      title: 'Постачальник',
      dataIndex: 'supplierName',
      key: 'supplierName',
      ellipsis: true,
    },
    {
      title: 'Дата',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 120,
      render: (mdValue: string) => dayjs(mdValue).format('DD.MM.YYYY'),
      sorter: (mdA, mdB) => mdA.orderDate.localeCompare(mdB.orderDate),
    },
    {
      title: 'Очікувана доставка',
      dataIndex: 'expectedDeliveryDate',
      key: 'expectedDeliveryDate',
      width: 160,
      render: (mdValue?: string) =>
        mdValue ? dayjs(mdValue).format('DD.MM.YYYY') : '—',
    },
    {
      title: 'Сума',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 140,
      align: 'right',
      render: (mdValue: number, mdRecord: PurchaseOrderListItem) =>
        `${mdValue.toLocaleString('uk-UA', { minimumFractionDigits: 2 })} ${mdRecord.currency}`,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 170,
      render: (mdValue: PurchaseOrderStatus) => (
        <Badge
          status={MD_STATUS_COLOR_MAP[mdValue] as 'default' | 'processing' | 'success' | 'error' | 'warning'}
          text={MD_STATUS_LABEL_MAP[mdValue]}
        />
      ),
    },
    {
      title: 'Дії',
      key: 'actions',
      width: 120,
      render: (_: unknown, mdRecord: PurchaseOrderListItem) => (
        <Space>
          <Button type="link" size="small" onClick={() => onViewOrder(mdRecord.id)}>
            Переглянути
          </Button>
          {mdRecord.status === 'Draft' && (
            <Button type="link" size="small" onClick={() => onEditOrder(mdRecord.id)}>
              Редагувати
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Замовлення постачальникам
        </Typography.Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={onCreateOrder}>
          Нове замовлення
        </Button>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Пошук за номером..."
          allowClear
          onSearch={(mdValue) => {
            setMdSearch(mdValue);
            setMdPage(1);
          }}
          style={{ width: 220 }}
        />
        <Select
          allowClear
          showSearch
          placeholder="Постачальник"
          filterOption={false}
          onSearch={setMdSupplierSearchText}
          onChange={(mdValue: string | undefined) => {
            setMdSupplierFilter(mdValue);
            setMdPage(1);
          }}
          options={mdSupplierOptions?.map((mdSup) => ({
            value: mdSup.id,
            label: `${mdSup.code} — ${mdSup.name}`,
          }))}
          style={{ width: 250 }}
        />
        <Select
          options={MD_STATUS_OPTIONS}
          value={mdStatusFilter}
          onChange={(mdValue) => {
            setMdStatusFilter(mdValue);
            setMdPage(1);
          }}
          style={{ width: 180 }}
        />
        <RangePicker
          format="DD.MM.YYYY"
          onChange={(mdDates) => {
            setMdDateRange(mdDates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null);
            setMdPage(1);
          }}
          placeholder={['Дата від', 'Дата до']}
        />
      </Space>

      <Table<PurchaseOrderListItem>
        columns={mdColumns}
        dataSource={mdOrdersData?.items}
        rowKey="id"
        loading={mdIsLoading}
        onRow={(mdRecord) => ({
          onClick: () => onViewOrder(mdRecord.id),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: mdPage,
          pageSize: mdPageSize,
          total: mdOrdersData?.totalCount,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          onChange: (mdNewPage, mdNewPageSize) => {
            setMdPage(mdNewPage);
            setMdPageSize(mdNewPageSize);
          },
        }}
        size="middle"
      />
    </>
  );
}
